/*
=============================================
   ENGAGEMENT BY WEEK  (professor portal view)
=============================================
Students down, calendar weeks across, tracked and self-logged minutes side by
side in each week, with a class row and per-student totals. Exports to CSV and
to XLSX.

⭐ THIS VIEW COMPUTES NOTHING. Every number comes from `GET /engagement`, which
already returns each student's `byWeek` with trackedMinutes / loggedMinutes /
totalMinutes, bucketed Monday-Sunday in the class's report time zone by
lib/engagement.js's gap rule. That is deliberate and it is the whole design:
FIN 397 is graded 80% on this number, and a second implementation of it in the
browser would eventually disagree with the one the gradebook uses. If a figure
here looks wrong, the fix belongs in lib/engagement.js, not here.

⚠️ TRACKED AND LOGGED ARE NEVER SILENTLY ADDED TOGETHER. Tracked is what the
add-in measured; logged is what the student typed in after the fact. They are
different kinds of evidence, so they get different columns and the total is a
third, clearly-labelled number. David's call, 2026-09-12. It also answers a
question he actually has: when the add-in is broken for someone, their weeks
read 0 tracked and n logged, which is visible at a glance.

⚠️ NAMES ARE SHOWN BY DEFAULT HERE, and the endpoint's default is the opposite.
`/engagement` returns "Student 42" labels unless asked with reveal=1, because
THAT screen gets projected in class. This one is a gradebook: a report full of
"Student 42" cannot be reconciled against a roster. The Hide names button is one
click away for when the projector is on, and the state is visible in the button.
*/

import { api } from "../scripts/api.js";

const esc = (t) => String(t ?? "").replace(/[<>&]/g, "");
const MIN = (v) => (v == null ? "" : (Math.round(Number(v) * 10) / 10).toString());
const HRS = (v) => (v == null ? "" : (Number(v) / 60).toFixed(1));

// "2026-09-08" -> "Sep 8". Built from the ISO parts rather than a Date, because
// new Date("2026-09-08") is UTC midnight and prints as Sep 7 anywhere west of
// Greenwich - which is every class this platform has.
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function weekLabel(iso) {
   const [y, m, d] = String(iso).split("-").map(Number);
   return `${MONTHS[(m || 1) - 1]} ${d}`;
}
function addDaysISO(iso, days) {
   const [y, m, d] = String(iso).split("-").map(Number);
   const dt = new Date(Date.UTC(y, m - 1, d));
   dt.setUTCDate(dt.getUTCDate() + days);
   return dt.toISOString().slice(0, 10);
}

class AppEngagementWeekly extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <h1 class="page-title">Engagement by week</h1>
         <p class="page-sub">Active minutes per calendar week (Monday to Sunday, in the
            class's time zone), measured by the add-in, with self-logged offline practice
            beside it. The same numbers the engagement report and the gradebook use.</p>

         <div class="sc-bar">
            <label>Class <select id="ew-class" class="sc-select"></select></label>
            <label>From <input type="date" id="ew-from" class="sc-select ew-date"></label>
            <label>To <input type="date" id="ew-to" class="sc-select ew-date"></label>
            <button id="ew-names" class="sc-btn ew-toggle">Hide names</button>
            <button id="ew-csv" class="sc-btn" disabled>CSV</button>
            <button id="ew-xlsx" class="sc-btn" disabled>Excel</button>
         </div>

         <div id="ew-status" class="sc-status"></div>

         <div id="ew-wrap" hidden>
            <div class="sc-sheet">
               <table class="sc-table ew-table">
                  <thead id="ew-head"></thead>
                  <tbody id="ew-body"></tbody>
                  <tfoot id="ew-foot"></tfoot>
               </table>
            </div>
            <p class="sc-legend">
               <span class="ew-key ew-key-t"></span>tracked by the add-in
               <span class="ew-key ew-key-l"></span>self-logged offline
               <span class="sc-legend-note" id="ew-meta"></span>
            </p>
         </div>`;

      this.classSel = this.querySelector("#ew-class");
      this.fromEl = this.querySelector("#ew-from");
      this.toEl = this.querySelector("#ew-to");
      this.reveal = true;

      this.classSel.addEventListener("change", () => this.load());
      this.fromEl.addEventListener("change", () => this.load());
      this.toEl.addEventListener("change", () => this.load());
      this.querySelector("#ew-names").addEventListener("click", () => {
         this.reveal = !this.reveal;
         this.querySelector("#ew-names").textContent = this.reveal ? "Hide names" : "Show names";
         this.load();
      });
      this.querySelector("#ew-csv").addEventListener("click", () => this.download("csv"));
      this.querySelector("#ew-xlsx").addEventListener("click", () => this.download("xlsx"));
      this.loadClasses();
   }

   show() { if (this.classSel && this.classSel.value) this.load(); }

   status(text, kind = "") {
      const el = this.querySelector("#ew-status");
      el.textContent = text || "";
      el.className = "sc-status " + kind;
   }

   async loadClasses() {
      try {
         const r = await api("/list-classes");
         if (!r.ok) throw new Error(`list-classes ${r.status}`);
         const { classes = [] } = await r.json();
         if (!classes.length) { this.status("No classes yet."); return; }
         this.classSel.innerHTML = classes.map(c =>
            `<option value="${c.class_id}">${esc(c.course_code)} · ${esc(c.section)} — ${esc(c.title || "")}</option>`
         ).join("");
         // FIN 397 grades 80% off this number, so it is the sensible landing class
         // when the professor teaches several.
         const fin397 = classes.find(c => String(c.course_code || "").replace(/\s+/g, "").toUpperCase() === "FIN397");
         if (fin397) this.classSel.value = String(fin397.class_id);
         this.load();
      } catch {
         this.status("Could not load your classes.", "err");
      }
   }

   async load() {
      const classId = this.classSel.value;
      if (!classId) return;
      this.status("Loading…");
      this.querySelector("#ew-csv").disabled = true;
      this.querySelector("#ew-xlsx").disabled = true;
      try {
         const p = new URLSearchParams({ class: classId });
         if (this.reveal) p.set("reveal", "1");
         if (this.fromEl.value) p.set("from", this.fromEl.value);
         // The window is half-open on the server ([from, to)), so a To of Sunday
         // would drop Sunday. Send the next day and the date the professor picked
         // is included, which is what picking it means.
         if (this.toEl.value) p.set("to", addDaysISO(this.toEl.value, 1));
         const r = await api(`/engagement?${p.toString()}`);
         if (r.status === 403) { this.status("You do not teach that class.", "err"); return; }
         if (!r.ok) throw new Error(`engagement ${r.status}`);
         this.data = await r.json();
         this.render();
      } catch (err) {
         this.status("Could not load engagement.", "err");
         console.error("engagement weekly load failed", err);
      }
   }

   /*
   Every week between the first and the last with activity, including the empty
   ones. A gap in a student's practice is the finding; showing only the weeks
   that happen to have data hides exactly the weeks worth asking about.
   */
   weekColumns(students) {
      const seen = new Set();
      for (const s of students) for (const w of s.byWeek || []) seen.add(w.weekStart);
      if (!seen.size) return [];
      const sorted = [...seen].sort();
      const out = [];
      for (let w = sorted[0]; w <= sorted[sorted.length - 1]; w = addDaysISO(w, 7)) out.push(w);
      return out;
   }

   rows() {
      const students = (this.data.students || []);
      const weeks = this.weekColumns(students);
      return {
         weeks,
         rows: students.map(s => {
            const byWeek = new Map((s.byWeek || []).map(w => [w.weekStart, w]));
            return {
               name: s.name, email: s.email, userId: s.userId,
               cells: weeks.map(w => {
                  const v = byWeek.get(w);
                  return {
                     week: w,
                     tracked: v ? Number(v.trackedMinutes || 0) : 0,
                     logged: v ? Number(v.loggedMinutes || 0) : 0,
                  };
               }),
               tracked: Number(s.trackedMinutes || 0),
               logged: Number(s.loggedMinutes || 0),
               total: Number(s.totalMinutes || 0),
            };
         }),
      };
   }

   render() {
      const { weeks, rows } = this.rows();
      const wrap = this.querySelector("#ew-wrap");
      if (!rows.length || !weeks.length) {
         wrap.hidden = true;
         this.status(rows.length
            ? "No activity recorded for this class in that window."
            : "No students enrolled in this class.");
         return;
      }
      this.status("");
      wrap.hidden = false;
      this.querySelector("#ew-csv").disabled = false;
      this.querySelector("#ew-xlsx").disabled = false;

      const m = this.data;
      this.querySelector("#ew-meta").textContent =
         `${m.tz || "America/Phoenix"} · weeks run ${m.weekBoundary || "Mon-Sun"} · `
         + `minutes, one decimal · ${rows.length} students`;

      /*
      A TWO-ROW HEADER, the way a spreadsheet does it: the week spans its pair of
      columns, and the pair says which number is which. One merged cell reading
      "182 / 30" would be unsortable and unpivotable the moment it reached Excel,
      which is where this report is going.
      */
      this.querySelector("#ew-head").innerHTML = `
         <tr>
            <th class="sc-corner ew-corner" rowspan="2">Student</th>
            ${weeks.map(w => `<th class="ew-week" colspan="2">${weekLabel(w)}</th>`).join("")}
            <th class="ew-total-head" colspan="3">Term total</th>
         </tr>
         <tr>
            ${weeks.map(() => `<th class="ew-sub ew-t">tracked</th><th class="ew-sub ew-l">logged</th>`).join("")}
            <th class="ew-sub ew-t">tracked</th><th class="ew-sub ew-l">logged</th><th class="ew-sub">total</th>
         </tr>`;

      this.querySelector("#ew-body").innerHTML = rows.map(r => `
         <tr>
            <th class="sc-rowhead" title="${esc(r.email || r.name)}">${esc(r.name)}</th>
            ${r.cells.map(c => `
               <td class="ew-num ew-t ${c.tracked ? "" : "ew-zero"}">${c.tracked ? MIN(c.tracked) : "—"}</td>
               <td class="ew-num ew-l ${c.logged ? "" : "ew-zero"}">${c.logged ? MIN(c.logged) : ""}</td>`).join("")}
            <td class="ew-num ew-t ew-term">${MIN(r.tracked)}</td>
            <td class="ew-num ew-l ew-term">${MIN(r.logged)}</td>
            <td class="ew-num ew-term ew-grand">${MIN(r.total)}</td>
         </tr>`).join("");

      // The class row: what the whole section did that week, which is the line
      // that tells you whether a quiet week was one student or all of them.
      const sum = (f) => rows.reduce((a, r) => a + f(r), 0);
      this.querySelector("#ew-foot").innerHTML = `
         <tr>
            <th class="sc-rowhead ew-foot-head">Class total</th>
            ${weeks.map((w, i) => `
               <td class="ew-num ew-t">${MIN(sum(r => r.cells[i].tracked))}</td>
               <td class="ew-num ew-l">${MIN(sum(r => r.cells[i].logged))}</td>`).join("")}
            <td class="ew-num ew-t ew-term">${MIN(sum(r => r.tracked))}</td>
            <td class="ew-num ew-l ew-term">${MIN(sum(r => r.logged))}</td>
            <td class="ew-num ew-term ew-grand">${MIN(sum(r => r.total))}</td>
         </tr>
         <tr>
            <th class="sc-rowhead ew-foot-head">Class hours</th>
            ${weeks.map((w, i) => `
               <td class="ew-num ew-t ew-hours">${HRS(sum(r => r.cells[i].tracked))}</td>
               <td class="ew-num ew-l ew-hours">${HRS(sum(r => r.cells[i].logged))}</td>`).join("")}
            <td class="ew-num ew-t ew-term ew-hours">${HRS(sum(r => r.tracked))}</td>
            <td class="ew-num ew-l ew-term ew-hours">${HRS(sum(r => r.logged))}</td>
            <td class="ew-num ew-term ew-grand ew-hours">${HRS(sum(r => r.total))}</td>
         </tr>`;
   }

   /* ---- Export -------------------------------------------------------------
   Built in the browser from the SAME object the table rendered, so the file and
   the screen cannot disagree - the failure mode of a second server endpoint that
   re-queries and re-rounds.

   The workbook carries TWO sheets. "By week" is the grid as you see it, for
   reading and printing. "Long" is one row per student per week, which is the
   shape a PivotTable wants; anyone who has tried to pivot a wide sheet with a
   two-row header knows why both are here.
   --------------------------------------------------------------------------- */
   sheetData() {
      const { weeks, rows } = this.rows();
      const cls = this.classSel.options[this.classSel.selectedIndex].textContent.trim();
      const head1 = ["Student", "Email", ...weeks.flatMap(w => [weekLabel(w), ""]),
                     "Term tracked", "Term logged", "Term total"];
      const head2 = ["", "", ...weeks.flatMap(() => ["tracked", "logged"]), "", "", ""];
      const wide = rows.map(r => [
         r.name, r.email || "",
         ...r.cells.flatMap(c => [c.tracked, c.logged]),
         r.tracked, r.logged, r.total,
      ]);
      const long = [["Student", "Email", "Week starting", "Tracked minutes", "Logged minutes", "Total minutes"]];
      for (const r of rows) {
         for (const c of r.cells) {
            long.push([r.name, r.email || "", c.week, c.tracked, c.logged,
                       Math.round((c.tracked + c.logged) * 10) / 10]);
         }
      }
      return { cls, weeks, head1, head2, wide, long };
   }

   fileStem() {
      const cls = this.classSel.options[this.classSel.selectedIndex].textContent;
      const slug = cls.split("·")[0].trim().replace(/\s+/g, "") || `class${this.classSel.value}`;
      const today = new Date().toISOString().slice(0, 10);
      return `${slug}-engagement-by-week-${today}`;
   }

   download(kind) {
      const { head1, head2, wide, long } = this.sheetData();
      if (kind === "csv") {
         const cell = v => {
            const s = v == null ? "" : String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
         };
         const csv = [head1, head2, ...wide].map(r => r.map(cell).join(",")).join("\n") + "\n";
         this.saveBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), `${this.fileStem()}.csv`);
         return;
      }
      /*
      ⚠️ SheetJS is loaded by professor.html / admin.html from a CDN, so it can be
      absent - a blocked CDN, an offline laptop. Falling back to CSV keeps the
      button honest rather than throwing into the console and doing nothing,
      which is how the Submit button failed silently in the pane on 9/7.
      */
      const XLSX = window.XLSX;
      if (!XLSX) {
         this.status("Excel export needs the spreadsheet library, which did not load. Sent CSV instead.", "err");
         return this.download("csv");
      }
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([head1, head2, ...wide]);
      // Merge each week's label across its two columns, and freeze the header
      // rows and the name column, so the sheet opens behaving like this screen.
      ws["!merges"] = head1.reduce((acc, v, i) => {
         if (i >= 2 && v && head1[i + 1] === "") acc.push({ s: { r: 0, c: i }, e: { r: 0, c: i + 1 } });
         return acc;
      }, []);
      ws["!freeze"] = { xSplit: 2, ySplit: 2 };
      ws["!cols"] = head1.map((h, i) => ({ wch: i === 0 ? 24 : i === 1 ? 28 : 10 }));
      XLSX.utils.book_append_sheet(wb, ws, "By week");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(long), "Long");
      XLSX.writeFile(wb, `${this.fileStem()}.xlsx`);
   }

   saveBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
   }
}

customElements.define("app-engagement-weekly", AppEngagementWeekly);
